import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api.js';
import { useFetch } from '../lib/useFetch.js';
import { Async } from '../components/States.js';
import { Modal } from '../components/Modal.js';
import { Dd } from '../components/Dropdown.js';
import { SignatureEditor, SignConfirmModal } from '../components/SignatureModals.js';
import { DateField } from '../components/DateTimeField.js';
import { getSignatureProfile, hasSavedSignature, signatureSynced } from '../lib/signature.js';
import { useSession, displayName } from '../auth/session.js';
import {
  SCALE_OPTS,
  answerText,
  answeredCount,
  isAcknowledgment,
  type AnsweredField,
  type AnsweredForm,
  type SubmissionAnswers,
} from '../lib/formAnswers.js';
import { timeOpts } from '../lib/timePrefs.js';

/** Sign-off task — shape from signoff.route serialize(). Mirrors the mockup's queue rows. */
/** A sign-off queue row; portal#61 - also built from a Forms Hub send to preview it. */
export interface Task {
  id: string;
  stage: string;
  signedAt: string | null;
  dueAt: string | null;
  submittedAt: string | null;
  patient: string | null;
  form: string | null;
  formId: string | null;
  packetId: string | null;
  submissionId: string | null;
  assignedTo: string | null;
}

interface OrgUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
}
const orgUserName = (u: OrgUser) => [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email;

/** Signature chain — every sign-off task on the submission (4+ supported). */
interface SubmissionSignature {
  id: string;
  stage: string;
  signedAt: string | null;
  createdAt: string;
  assignedUser: { firstName: string | null; lastName: string | null } | null;
}
interface SubmissionDetail {
  signoffTasks: SubmissionSignature[];
}
const fmtSignedAt = (s: string) =>
  new Date(s).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...timeOpts(),
  });

interface FormDetail {
  name: string;
  description: string | null;
  fields: { id: string; label: string; fieldType: string; position: number; required: boolean }[];
}
interface PacketDetail {
  name: string;
  description: string | null;
  packetForms: { form: { id: string; name: string } }[];
}

/** One rendered (read-only) form control per field — the fill-page look of the
 *  Form Builder's preview rather than a field list. The EHR stores label +
 *  fieldType only, so option sets use the builder's standard scales. */
function PreviewField({ f, i }: { f: FormDetail['fields'][number]; i: number }) {
  const label = (
    <div className="fprv-label">
      {i + 1}. {f.label}
      {f.required && <span className="fprv-req">*</span>}
    </div>
  );
  switch (f.fieldType) {
    case 'scale':
      return (
        <div className="fprv-field">
          {label}
          <div className="fprv-opts">
            {SCALE_OPTS.map((o, n) => (
              <label className="fprv-opt" key={o}>
                <input type="radio" disabled name={`pf-${f.id}`} /> {n} — {o}
              </label>
            ))}
          </div>
        </div>
      );
    case 'radio':
      return (
        <div className="fprv-field">
          {label}
          <div className="fprv-opts">
            <label className="fprv-opt">
              <input type="radio" disabled name={`pf-${f.id}`} /> Yes
            </label>
            <label className="fprv-opt">
              <input type="radio" disabled name={`pf-${f.id}`} /> No
            </label>
          </div>
        </div>
      );
    case 'checkbox':
      // Screener yes/no items were seeded as checkbox; the Form Builder
      // renders questions as Yes/No radios and consent statements as
      // acknowledgment checkboxes.
      if (f.label.trim().endsWith('?')) {
        return (
          <div className="fprv-field">
            {label}
            <div className="fprv-opts">
              <label className="fprv-opt">
                <input type="radio" disabled name={`pf-${f.id}`} /> Yes
              </label>
              <label className="fprv-opt">
                <input type="radio" disabled name={`pf-${f.id}`} /> No
              </label>
            </div>
          </div>
        );
      }
      return (
        <label className="fprv-chk">
          <input type="checkbox" disabled />
          <span>
            {f.label}
            {f.required && <span className="fprv-req">*</span>}
          </span>
        </label>
      );
    case 'textarea':
      return (
        <div className="fprv-field">
          {label}
          <textarea disabled placeholder="Client response…" />
        </div>
      );
    case 'date':
      return (
        <div className="fprv-field">
          {label}
          <DateField value="" onChange={() => {}} disabled />
        </div>
      );
    case 'number':
      return (
        <div className="fprv-field">
          {label}
          <input type="number" disabled placeholder="0" />
        </div>
      );
    case 'select':
      return (
        <div className="fprv-field">
          {label}
          <Dd
            value=""
            disabled
            searchable={false}
            placeholder="— Select —"
            options={[]}
            onChange={() => {}}
          />
        </div>
      );
    case 'signature':
      return (
        <div className="fprv-field">
          {label}
          <div className="fprv-sig">✍ Signature — captured in the client portal</div>
        </div>
      );
    default:
      return (
        <div className="fprv-field">
          {label}
          <input type="text" disabled placeholder="Short answer" />
        </div>
      );
  }
}

/** portal#42 - one question with the client's answer, read-only. */
function AnswerField({ f, i }: { f: AnsweredField; i: number }) {
  const req = f.required && <span className="fprv-req">*</span>;
  if (isAcknowledgment(f)) {
    // Not `disabled`: a disabled box greys out and the client's tick is hard to read.
    return (
      <label className="fprv-chk fprv-chk-answer">
        <input
          type="checkbox"
          checked={f.value === true}
          readOnly
          aria-readonly="true"
          tabIndex={-1}
          onClick={(e) => e.preventDefault()}
        />
        <span>
          {f.label}
          {req}
        </span>
      </label>
    );
  }
  const text = answerText(f);
  const cls = ['fprv-answer', !text && 'empty', text && f.fieldType === 'signature' && 'sig']
    .filter(Boolean)
    .join(' ');
  return (
    <div className="fprv-field">
      <div className="fprv-label">
        {i + 1}. {f.label}
        {req}
      </div>
      <div className={cls}>{text || 'No answer'}</div>
    </div>
  );
}

/** A form's questions: with the client's answers when there are any, else the blank
 *  read-only preview. */
function FormBody({
  answered,
  fields,
}: {
  answered: AnsweredForm | null;
  fields: FormDetail['fields'];
}) {
  if (answered) {
    if (answered.fields.length === 0) {
      return <div className="fp-empty">No field definitions available for this form.</div>;
    }
    const n = answered.fields.length;
    return (
      <>
        <div className="fprv-count">
          {n} field{n !== 1 ? 's' : ''} · client's answers · {answeredCount(answered)} answered
          {/* ehr#593: the questions as the client filled them, whatever the form says today */}
          {answered.asFilled && (
            <span title="The questions the client saw when they filled it. Questions added to the form since aren't shown.">
              {' '}
              · as the client filled it
            </span>
          )}
        </div>
        <div className="fprv">
          {answered.fields.map((f, i) => (
            <AnswerField f={f} i={i} key={f.fieldId ?? `x${i}`} />
          ))}
        </div>
      </>
    );
  }
  if (fields.length === 0) {
    return <div className="fp-empty">No field definitions available for this form.</div>;
  }
  return (
    <>
      <div className="fprv-count">
        {fields.length} field{fields.length !== 1 ? 's' : ''} · read-only preview
      </div>
      <div className="fprv">
        {fields.map((f, i) => (
          <PreviewField f={f} i={i} key={f.id} />
        ))}
      </div>
    </>
  );
}

/** Mockup openFormPreview port — renders the queue row's form (or packet)
 *  definition from the org's forms library, the same catalog the Form Builder
 *  (forms.credifyfast.com) manages. portal#42: with the client's answers, and each form
 *  of a bundle opens on its own (View). */
export function FormPreviewModal({
  task,
  onClose,
  onSign,
}: {
  task: Task;
  onClose: () => void;
  /** Present for unsigned tasks — the queue's Sign action lives at the bottom
   *  of this modal (opens the attest/co-sign confirmation). */
  onSign?: () => void;
}) {
  const [form, setForm] = useState<FormDetail | null>(null);
  const [packet, setPacket] = useState<PacketDetail | null>(null);
  const [sigs, setSigs] = useState<SubmissionSignature[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  // portal#42 - the client's answers (none for a role without PHI access, or before the
  // client submits), the bundle form open on its own, and the definitions fetched for a
  // bundle form when there are no answers to show with it.
  const [answers, setAnswers] = useState<SubmissionAnswers | null>(null);
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const [memberDefs, setMemberDefs] = useState<Record<string, FormDetail>>({});

  useEffect(() => {
    let live = true;
    const req = task.formId
      ? api.get<FormDetail>(`/forms/${task.formId}`).then((f) => live && setForm(f))
      : task.packetId
        ? api.get<PacketDetail>(`/packets/${task.packetId}`).then((p) => live && setPacket(p))
        : Promise.reject(new Error('This sign-off has no linked form definition.'));
    req
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => live && setLoading(false));
    // Signature chain — best-effort (the preview still renders without it).
    if (task.submissionId) {
      api
        .get<SubmissionDetail>(`/submissions/${task.submissionId}`)
        .then((s) => live && setSigs(s.signoffTasks ?? []))
        .catch(() => undefined);
      api
        .get<SubmissionAnswers>(`/submissions/${task.submissionId}/answers`)
        .then((a) => live && setAnswers(a))
        .catch(() => undefined);
    }
    return () => {
      live = false;
    };
  }, [task]);

  const fields = form?.fields ?? [];
  const memberForms = packet?.packetForms ?? [];
  const shown = answers?.hasAnswers ? answers : null;
  const formAnswered =
    form && shown
      ? (shown.forms.find((f) => f.formId === task.formId) ?? shown.forms[0] ?? null)
      : null;
  // A bundle's forms, with their answers when the client has submitted.
  const rows: { formId: string | null; name: string; answered: AnsweredForm | null }[] = packet
    ? shown
      ? shown.forms.map((f) => ({ formId: f.formId, name: f.name, answered: f }))
      : memberForms.map((pf) => ({ formId: pf.form.id, name: pf.form.name, answered: null }))
    : [];
  const idx = openIdx ?? 0;
  const open = openIdx !== null ? (rows[openIdx] ?? null) : null;

  function openRow(i: number) {
    setOpenIdx(i);
    const r = rows[i];
    const id = r?.formId;
    if (!r || r.answered || !id || memberDefs[id]) return;
    api
      .get<FormDetail>(`/forms/${id}`)
      .then((f) => setMemberDefs((m) => ({ ...m, [id]: f })))
      .catch(() =>
        setMemberDefs((m) => ({ ...m, [id]: { name: r.name, description: null, fields: [] } })),
      );
  }

  return (
    <Modal
      title="Form Preview"
      className="fh-preview-xl"
      onClose={onClose}
      actions={
        <>
          <button className="fh-btn" onClick={onClose}>
            Close
          </button>
          {onSign ? (
            <button className="btn sm primary" onClick={onSign}>
              Sign
            </button>
          ) : (
            <button className="btn sm" disabled>
              ✓ Done
            </button>
          )}
        </>
      }
    >
      {loading ? (
        <p className="muted">Loading form…</p>
      ) : error ? (
        <div className="fp-empty">{error}</div>
      ) : (
        <>
          <div className="fp-meta">
            <div className="fp-meta-info">
              <div className="fp-meta-name">{form?.name ?? packet?.name ?? task.form}</div>
              <div className="fp-meta-desc">{form?.description ?? packet?.description ?? ''}</div>
            </div>
            <span className={`fp-type-badge ${packet ? 'packet' : 'form'}`}>
              {packet ? 'Bundle' : 'Form'}
            </span>
          </div>
          {form && <FormBody answered={formAnswered} fields={fields} />}
          {packet && !open && (
            <>
              <div className="fprv-count">
                {rows.length} form{rows.length !== 1 ? 's' : ''} in this bundle
                {shown ? " · View opens each with the client's answers" : ''}
              </div>
              <div className="fp-field-list">
                {rows.map((r, i) => (
                  <div className="fp-field fp-field-row" key={r.formId ?? `x${i}`}>
                    <span className="fp-field-num">{i + 1}</span>
                    <span className="fp-field-label">{r.name}</span>
                    {r.answered && (
                      <span className="fp-field-meta">
                        {answeredCount(r.answered)} of {r.answered.fields.length} answered
                      </span>
                    )}
                    <button
                      type="button"
                      className="fh-btn sm"
                      aria-label={`View ${r.name}`}
                      onClick={() => openRow(i)}
                    >
                      View
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
          {packet && open && (
            <>
              <div className="fp-sub-head">
                <button type="button" className="fp-back" onClick={() => setOpenIdx(null)}>
                  ← All forms in this bundle
                </button>
                <span className="fp-sub-pos">
                  Form {idx + 1} of {rows.length}
                </span>
                <span className="fp-sub-nav">
                  <button
                    type="button"
                    className="fh-btn sm"
                    disabled={idx === 0}
                    onClick={() => openRow(idx - 1)}
                  >
                    ‹ Previous
                  </button>
                  <button
                    type="button"
                    className="fh-btn sm"
                    disabled={idx >= rows.length - 1}
                    onClick={() => openRow(idx + 1)}
                  >
                    Next ›
                  </button>
                </span>
              </div>
              <div className="fp-sub-name">{open.name}</div>
              {open.answered ? (
                <FormBody answered={open.answered} fields={[]} />
              ) : open.formId && memberDefs[open.formId] ? (
                <FormBody answered={null} fields={memberDefs[open.formId]!.fields} />
              ) : (
                <p className="muted">Loading form…</p>
              )}
            </>
          )}
          {sigs.length > 0 && (
            <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
              <div className="section-label">
                Signatures ({sigs.filter((s) => s.signedAt).length}/{sigs.length})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {sigs.map((s, i) => (
                  <div
                    key={s.id}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}
                  >
                    <span className="fp-field-num">{i + 1}</span>
                    <span style={{ fontWeight: 600 }}>
                      {s.assignedUser
                        ? `${s.assignedUser.firstName ?? ''} ${s.assignedUser.lastName ?? ''}`.trim()
                        : 'Unassigned'}
                    </span>
                    <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                      {s.stage === 'clinician' ? 'Clinician' : 'Co-sign'}
                    </span>
                    <span style={{ marginLeft: 'auto', fontSize: 12 }}>
                      {s.signedAt ? (
                        <span style={{ color: 'var(--green-primary)', fontWeight: 600 }}>
                          ✓ Signed {fmtSignedAt(s.signedAt)}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text-muted-2)' }}>Pending</span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

// Mockup SIGNOFF_PAGE_SIZE — the queue paginates at 5 rows.
const PAGE_SIZE = 5;
// Submitted and Due read alike, "Jul 12" (no year). fbs#151: Due was ISO "2026-07-14".
const fmtSubmitted = (s: string | null) =>
  s ? new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';
const fmtDue = fmtSubmitted;
const overdueOf = (t: Task) => !t.signedAt && !!t.dueAt && new Date(t.dueAt) < new Date();
const statusOf = (t: Task): 'signed' | 'overdue' | 'pending' =>
  t.signedAt ? 'signed' : overdueOf(t) ? 'overdue' : 'pending';

// Mockup pagerRange: first / last / current±1 with … gaps.
function pagerRange(page: number, total: number): (number | '…')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out: (number | '…')[] = [1];
  const start = Math.max(2, page - 1);
  const end = Math.min(total - 1, page + 1);
  if (start > 2) out.push('…');
  for (let i = start; i <= end; i++) out.push(i);
  if (end < total - 1) out.push('…');
  out.push(total);
  return out;
}

/** Sign-off flow step — a saved-signature editor, or the auto-apply confirmation. */
type SignFlow =
  { task: Task; step: 'confirm' } | { task: Task; step: 'editor'; context: 'sign' | 'create' };

export function SignoffTab() {
  const { user } = useSession();
  // Saved signatures persist per signer (see lib/signature.ts).
  const userKey = user?.id ?? user?.email ?? 'me';
  const signerLabel = user ? displayName(user.name, user.email) : 'You';
  const roleNote =
    user?.role === 'clinician'
      ? 'Your items only'
      : user?.role === 'supervisor'
        ? 'Your queue + co-signs'
        : null;
  // The queue endpoint returns unsigned tasks by default; signed tasks come via
  // ?signed=true. Fetch both so the Status filter (Pending/Overdue/Signed) and the
  // per-row pills work client-side, matching the mockup's single-render queue.
  const queue = useFetch<Task[]>(
    () =>
      Promise.all([
        api.get<Task[]>('/signoff/queue'),
        api.get<Task[]>('/signoff/queue?signed=true'),
      ]).then(([pending, signed]) => [...pending, ...signed]),
    [],
  );

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [signer, setSigner] = useState('');
  const [page, setPage] = useState(1);
  const [flow, setFlow] = useState<SignFlow | null>(null);
  const [viewing, setViewing] = useState<Task | null>(null);
  const [coSign, setCoSign] = useState(false);
  const [coSignUserId, setCoSignUserId] = useState('');
  const [busy, setBusy] = useState(false);
  // Co-signer picker — any org user can be chained as the next signature.
  const orgUsers = useFetch<OrgUser[]>(() => api.get('/settings/users'), []);

  // Lightweight toast (mockup showToast) — signature saved / form signed.
  const toastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [toastMsg, setToastMsg] = useState('');
  const [toastShow, setToastShow] = useState(false);
  const toast = (m: string) => {
    setToastMsg(m);
    setToastShow(true);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastShow(false), 2200);
  };

  const reset = () => setPage(1);

  // Queue Sign → route through the saved-signature flow. ehr#278 (Chase, 10-13): a signature
  // on file fills in by itself, whatever the preference: the confirm window shows it, and
  // "Use a different signature" opens the options. With none on file the options open: auto
  // mode creates and saves one first; each-time builds one for this sign-off.
  async function startSign(task: Task) {
    setCoSign(false);
    setCoSignUserId('');
    // ehr#404: wait for the page-load signature sync (instant once it's done), so a signature
    // saved in another browser is the one that fills in.
    await signatureSynced(userKey).catch(() => undefined);
    const prof = getSignatureProfile(userKey);
    if (hasSavedSignature(prof)) setFlow({ task, step: 'confirm' });
    else if (prof.mode === 'auto') setFlow({ task, step: 'editor', context: 'create' });
    else setFlow({ task, step: 'editor', context: 'sign' });
  }

  // Terminal action for every path — records the attestation via the existing
  // sign endpoint (co-sign preserved). The signature image itself is held client
  // side for now (see lib/signature.ts).
  async function commitSignoff(task: Task) {
    if (coSign && !coSignUserId && task.stage !== 'clinician') {
      toast('Select a co-signer first.');
      return;
    }
    setBusy(true);
    try {
      await api.post(`/signoff/${task.id}/sign`, {
        coSign,
        ...(coSign && coSignUserId ? { coSignUserId } : {}),
      });
      setFlow(null);
      setCoSign(false);
      setCoSignUserId('');
      queue.reload();
      toast(`"${task.form}" signed for ${task.patient}.`);
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  // Co-sign controls — shared by the editor (each-time) and confirm (auto) paths.
  const coSignFields = flow && (
    <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
      <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        <input
          type="checkbox"
          style={{ width: 'auto' }}
          checked={coSign}
          onChange={(e) => setCoSign(e.target.checked)}
        />
        Require another co-sign
      </label>
      {coSign && (
        <div className="field-group" style={{ maxWidth: 280, marginTop: 8 }}>
          <label>Co-signer</label>
          <Dd
            value={coSignUserId}
            onChange={setCoSignUserId}
            ariaLabel="Co-signer"
            options={[
              {
                value: '',
                label:
                  flow.task.stage === 'clinician'
                    ? 'Org-chart supervisor (default)'
                    : '— Select co-signer —',
              },
              ...(orgUsers.data ?? []).map((u) => ({ value: u.id, label: orgUserName(u) })),
            ]}
          />
        </div>
      )}
    </div>
  );

  return (
    <div className="fh-card">
      <div className="fh-card-head">
        <h3>Signoff Queue</h3>
        {roleNote && (
          <span className="pill muted" style={{ fontSize: 11 }}>
            {roleNote}
          </span>
        )}
      </div>

      <Async state={queue}>
        {(all) => {
          // ehr#494: every sign-off is listed. It used to show only the Form Builder's system forms
          // (and bundles), from when no practice had forms of its own; a practice form's submission
          // (e.g. "01 - Demographics") waited for sign-off but never appeared here.
          const visible = all;
          const signers = [
            ...new Set(visible.map((t) => t.assignedTo).filter(Boolean)),
          ] as string[];
          const q = search.trim().toLowerCase();
          const filtered = visible.filter((t) => {
            if (
              q &&
              !(t.patient ?? '').toLowerCase().includes(q) &&
              !(t.form ?? '').toLowerCase().includes(q)
            )
              return false;
            if (status && statusOf(t) !== status) return false;
            if (signer && t.assignedTo !== signer) return false;
            return true;
          });
          const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
          const safePage = Math.min(page, totalPages);
          const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
          const first = filtered.length ? (safePage - 1) * PAGE_SIZE + 1 : 0;
          const last = Math.min(safePage * PAGE_SIZE, filtered.length);

          return (
            <>
              <div className="card-body" style={{ paddingBottom: 0 }}>
                <div className="filter-bar">
                  <input
                    type="text"
                    placeholder="Search client or form…"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      reset();
                    }}
                  />
                  <Dd
                    value={status}
                    onChange={(v) => {
                      setStatus(v);
                      reset();
                    }}
                    searchable={false}
                    ariaLabel="Filter by status"
                    options={[
                      { value: '', label: 'All statuses' },
                      { value: 'pending', label: 'Pending' },
                      { value: 'overdue', label: 'Overdue' },
                      { value: 'signed', label: 'Signed' },
                    ]}
                  />
                  <Dd
                    value={signer}
                    onChange={(v) => {
                      setSigner(v);
                      reset();
                    }}
                    searchable={false}
                    ariaLabel="Filter by assigned signer"
                    options={[
                      { value: '', label: 'All signers' },
                      ...signers.map((s) => ({ value: s, label: s })),
                    ]}
                  />
                </div>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Client</th>
                      <th>Form</th>
                      <th>Submitted</th>
                      <th>Assigned Signer</th>
                      <th>Due</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageItems.length === 0 ? (
                      <tr>
                        <td
                          colSpan={7}
                          style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted-2)' }}
                        >
                          No items match the current filters.
                        </td>
                      </tr>
                    ) : (
                      pageItems.map((t) => {
                        const st = statusOf(t);
                        const over = st === 'overdue';
                        return (
                          <tr key={t.id}>
                            <td>
                              <strong>
                                <span className="fh-plink">{t.patient ?? '—'}</span>
                              </strong>
                            </td>
                            <td>{t.form ?? '—'}</td>
                            <td style={{ color: 'var(--text-muted)' }}>
                              {fmtSubmitted(t.submittedAt)}
                            </td>
                            <td>{t.assignedTo ?? 'Unassigned'}</td>
                            {/* Mockup: overdue due-dates inherit the row color (its --red is
                                undefined); signed/pending stay muted. */}
                            <td style={{ color: over ? 'inherit' : 'var(--text-muted)' }}>
                              {fmtDue(t.dueAt)}
                            </td>
                            <td>
                              {st === 'signed' ? (
                                <span className="pill green">Signed</span>
                              ) : over ? (
                                <span className="pill red">Overdue</span>
                              ) : (
                                <span className="pill amber">Pending</span>
                              )}
                            </td>
                            <td>
                              <div className="actions">
                                {/* Row-level Sign only while unsigned; signed
                                    rows show View only (✓ Done lives in the
                                    preview modal's action bar). */}
                                {!t.signedAt && (
                                  <button className="btn sm primary" onClick={() => startSign(t)}>
                                    Sign
                                  </button>
                                )}
                                <button className="btn sm" onClick={() => setViewing(t)}>
                                  View
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {filtered.length > PAGE_SIZE && (
                <div className="pager">
                  <div className="pager-count">
                    Showing{' '}
                    <strong>
                      {first}–{last}
                    </strong>{' '}
                    of <strong>{filtered.length}</strong> sign-offs
                  </div>
                  <div className="pager-pages">
                    <button
                      className="pager-btn"
                      disabled={safePage === 1}
                      onClick={() => setPage(safePage - 1)}
                      aria-label="Previous page"
                    >
                      ‹
                    </button>
                    {pagerRange(safePage, totalPages).map((n, i) =>
                      n === '…' ? (
                        <span className="pager-gap" key={`gap-${i}`}>
                          …
                        </span>
                      ) : (
                        <button
                          key={n}
                          className={`pager-btn${n === safePage ? ' active' : ''}`}
                          onClick={() => setPage(n)}
                          aria-current={n === safePage ? 'page' : undefined}
                          aria-label={`Page ${n}`}
                        >
                          {n}
                        </button>
                      ),
                    )}
                    <button
                      className="pager-btn"
                      disabled={safePage === totalPages}
                      onClick={() => setPage(safePage + 1)}
                      aria-label="Next page"
                    >
                      ›
                    </button>
                  </div>
                </div>
              )}
            </>
          );
        }}
      </Async>

      {/* Each-time / create path — build (and optionally save) a signature. Any
          stage may chain a further co-sign, so a submission can collect an
          unbounded number of signatures (4+). */}
      {flow?.step === 'editor' && (
        <SignatureEditor
          userKey={userKey}
          context={flow.context}
          task={flow.task}
          onClose={() => setFlow(null)}
          onToast={toast}
          onSign={() => commitSignoff(flow.task)}
          onCreate={() => setFlow({ task: flow.task, step: 'confirm' })}
          signExtra={coSignFields}
        />
      )}

      {/* Auto-apply path — confirm the saved signature and attest. */}
      {flow?.step === 'confirm' && (
        <SignConfirmModal
          userKey={userKey}
          task={flow.task}
          signerLabel={signerLabel}
          submitted={fmtSubmitted(flow.task.submittedAt)}
          due={fmtDue(flow.task.dueAt)}
          onClose={() => setFlow(null)}
          onUseDifferent={() => setFlow({ task: flow.task, step: 'editor', context: 'sign' })}
          onSign={() => commitSignoff(flow.task)}
          busy={busy}
          extra={coSignFields}
        />
      )}

      {viewing && (
        <FormPreviewModal
          task={viewing}
          onClose={() => setViewing(null)}
          onSign={
            viewing.signedAt
              ? undefined
              : () => {
                  const t = viewing;
                  setViewing(null);
                  startSign(t);
                }
          }
        />
      )}

      <div className={`app-toast ${toastShow ? 'show' : ''}`} role="status" aria-live="polite">
        {toastMsg}
      </div>
    </div>
  );
}
